// src/pages/legal/PrivacyPolicy.js
import React from 'react'
import { Box, Container, Typography, Card, CardContent, Divider, Stack } from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'

export default function PrivacyPolicy() {
    const effectiveDate = 'January 5, 2026' // change when needed
    const supportEmail = 'support@mojopayr.com' // change when needed

    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    const sectionTitleSx = {
        fontWeight: 900,
        color: 'rgba(255,255,255,0.92)',
        mt: 2.4,
        mb: 0.8,
        letterSpacing: 0.2,
    }

    const bodySx = {
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.9,
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    }

    const liSx = { mb: 0.8 }

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                <Typography component="h1" variant="h4" sx={{ fontWeight: 950, color: 'white', mb: 1 }}>
                    {tt('legal.privacy.title', 'Privacy Policy', 'Politique de confidentialité')}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ mb: 3, alignItems: { sm: 'center' } }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {tt(
                            'legal.privacy.subtitle',
                            'How MojoPayr collects, uses, and protects your information.',
                            'Comment MojoPayr collecte, utilise et protège vos informations.'
                        )}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {tt('legal.privacy.effective', 'Effective Date:', 'Date d’effet :')} {effectiveDate}
                    </Typography>
                </Stack>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.intro',
                                'MojoPayr values your privacy. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website, mobile applications, and services.',
                                'MojoPayr respecte votre vie privée. Cette politique explique comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous utilisez notre site, nos applications mobiles et nos services.'
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s1', '1. Information We Collect', '1. Informations que nous collectons')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s1.p1',
                                'We collect information you provide directly and information generated through your use of the service.',
                                'Nous collectons les informations que vous fournissez directement et celles générées par votre utilisation du service.'
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={{ marginBottom: 10 }}>
                                <strong style={{ color: 'rgba(255,255,255,0.92)' }}>
                                    {tt('legal.privacy.s1.personal.label', 'Personal information:', 'Informations personnelles :')}
                                </strong>{' '}
                                {tt(
                                    'legal.privacy.s1.personal.text',
                                    'full name, email address, phone number, date of birth, and other account details.',
                                    'nom complet, adresse e-mail, numéro de téléphone, date de naissance et autres informations de compte.'
                                )}
                            </li>

                            <li style={{ marginBottom: 10 }}>
                                <strong style={{ color: 'rgba(255,255,255,0.92)' }}>
                                    {tt('legal.privacy.s1.verify.label', 'Verification data:', 'Données de vérification :')}
                                </strong>{' '}
                                {tt(
                                    'legal.privacy.s1.verify.text',
                                    'government-issued identification and proof of address when required for identity verification.',
                                    "pièce d’identité officielle et justificatif de domicile lorsque nécessaire pour vérifier l’identité."
                                )}
                            </li>

                            <li style={{ marginBottom: 10 }}>
                                <strong style={{ color: 'rgba(255,255,255,0.92)' }}>
                                    {tt('legal.privacy.s1.tx.label', 'Transaction data:', 'Données de transaction :')}
                                </strong>{' '}
                                {tt(
                                    'legal.privacy.s1.tx.text',
                                    'wallet activity, transaction history, and payment-related records.',
                                    'activité du portefeuille, historique des transactions et enregistrements liés aux paiements.'
                                )}
                            </li>

                            <li style={{ marginBottom: 10 }}>
                                <strong style={{ color: 'rgba(255,255,255,0.92)' }}>
                                    {tt('legal.privacy.s1.tech.label', 'Technical data:', 'Données techniques :')}
                                </strong>{' '}
                                {tt(
                                    'legal.privacy.s1.tech.text',
                                    'device type, operating system, IP address, and app usage data.',
                                    "type d’appareil, système d’exploitation, adresse IP et données d’utilisation de l’application."
                                )}
                            </li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s2', '2. How We Use Your Information', '2. Comment nous utilisons vos informations')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s2.p1',
                                'We use your information to provide and protect the service. This includes:',
                                'Nous utilisons vos informations pour fournir et protéger le service, notamment :'
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.privacy.s2.li1', 'Creating and managing your account', 'Créer et gérer votre compte')}</li>
                            <li style={liSx}>{tt('legal.privacy.s2.li2', 'Processing transactions and payments', 'Traiter les transactions et les paiements')}</li>
                            <li style={liSx}>{tt('legal.privacy.s2.li3', 'Verifying your identity', 'Vérifier votre identité')}</li>
                            <li style={liSx}>{tt('legal.privacy.s2.li4', 'Preventing fraud and unauthorized access', 'Prévenir la fraude et les accès non autorisés')}</li>
                            <li style={liSx}>{tt('legal.privacy.s2.li5', 'Complying with legal and regulatory requirements', 'Respecter les obligations légales et réglementaires')}</li>
                            <li style={liSx}>{tt('legal.privacy.s2.li6', 'Improving our services and user experience', "Améliorer nos services et l’expérience utilisateur")}</li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s3', '3. How We Share Information', '3. Partage des informations')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s3.p1',
                                'We do not sell your personal data. We may share information when needed to operate the service and meet legal obligations, such as with:',
                                'Nous ne vendons pas vos données personnelles. Nous pouvons partager des informations lorsque nécessaire pour exploiter le service et respecter la loi, notamment avec :'
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.privacy.s3.li1', 'Payment processors and banking partners', 'Les prestataires de paiement et partenaires bancaires')}</li>
                            <li style={liSx}>{tt('legal.privacy.s3.li2', 'Identity verification providers', "Les prestataires de vérification d’identité")}</li>
                            <li style={liSx}>{tt('legal.privacy.s3.li3', 'Regulatory authorities when required by law', 'Les autorités de régulation lorsque requis par la loi')}</li>
                            <li style={liSx}>{tt('legal.privacy.s3.li4', 'Law enforcement agencies upon lawful request', "Les forces de l’ordre sur demande légale")}</li>
                        </Box>

                        <Typography sx={bodySx} style={{ marginTop: 10 }}>
                            {tt(
                                'legal.privacy.s3.p2',
                                'Third parties that process data on our behalf are required to protect it and use it only for the purposes we specify.',
                                'Les tiers qui traitent des données pour notre compte doivent les protéger et les utiliser uniquement aux fins que nous définissons.'
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s4', '4. Data Retention', '4. Conservation des données')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s4.p1',
                                'We retain your information as long as needed to provide the service and as required by applicable financial and regulatory laws. We may also retain certain records for fraud prevention, dispute resolution, and audit purposes. When information is no longer required, it is securely deleted or anonymized.',
                                "Nous conservons vos informations aussi longtemps que nécessaire pour fournir le service et conformément aux lois financières et réglementaires applicables. Nous pouvons aussi conserver certains dossiers pour prévenir la fraude, résoudre les litiges et effectuer des audits. Lorsque les informations ne sont plus nécessaires, elles sont supprimées de façon sécurisée ou anonymisées."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s5', '5. Data Security', '5. Sécurité des données')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s5.p1',
                                'We use security measures such as encryption, secure servers, monitoring, and access controls. No system is 100% secure, but we work to protect your information.',
                                'Nous utilisons des mesures de sécurité comme le chiffrement, des serveurs sécurisés, la surveillance et des contrôles d’accès. Aucun système n’est sûr à 100 %, mais nous faisons notre possible pour protéger vos informations.'
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s6', '6. Your Rights', '6. Vos droits')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s6.p1',
                                'Depending on your location and applicable law, you may have the right to:',
                                'Selon votre lieu de résidence et la loi applicable, vous pouvez avoir le droit de :'
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.privacy.s6.li1', 'Access your personal data', 'Accéder à vos données personnelles')}</li>
                            <li style={liSx}>{tt('legal.privacy.s6.li2', 'Correct inaccurate information', 'Corriger des informations inexactes')}</li>
                            <li style={liSx}>{tt('legal.privacy.s6.li3', 'Request deletion of your data (where allowed)', 'Demander la suppression de vos données (si autorisé)')}</li>
                            <li style={liSx}>{tt('legal.privacy.s6.li4', 'Withdraw consent where applicable', 'Retirer votre consentement lorsque applicable')}</li>
                        </Box>

                        <Typography sx={bodySx} style={{ marginTop: 10 }}>
                            {tt(
                                'legal.privacy.s6.p2',
                                'To request any of these, contact us using the email below.',
                                'Pour exercer ces droits, contactez-nous via l’e-mail ci-dessous.'
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s7', "7. Children’s Privacy", '7. Confidentialité des enfants')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s7.p1',
                                'MojoPayr is not intended for users under 18 years of age. We do not knowingly collect personal information from minors.',
                                "MojoPayr n’est pas destiné aux utilisateurs de moins de 18 ans. Nous ne collectons pas sciemment des informations personnelles auprès de mineurs."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s8', '8. Changes to This Policy', '8. Modifications de cette politique')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s8.p1',
                                'We may update this Privacy Policy from time to time. We will post changes on this page and update the effective date.',
                                "Nous pouvons mettre à jour cette politique de confidentialité. Nous publierons les changements sur cette page et mettrons à jour la date d’effet."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.privacy.s9', '9. Contact Us', '9. Contact')}
                        </Typography>

                        <Typography sx={bodySx}>
                            {tt(
                                'legal.privacy.s9.p1',
                                'If you have questions about this Privacy Policy, contact us at:',
                                'Si vous avez des questions sur cette politique, contactez-nous à :'
                            )}
                        </Typography>

                        <Typography sx={{ ...bodySx, mt: 1 }}>
                            {tt('legal.privacy.emailLabel', 'Email:', 'E-mail :')}{' '}
                            <Box
                                component="a"
                                href={`mailto:${supportEmail}`}
                                sx={{
                                    color: 'rgba(255,255,255,0.92)',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                }}
                            >
                                {supportEmail}
                            </Box>
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

